import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { ShieldCheck } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { pcmpEditGrantQueryOptions } from '../../api/pcmpEditPermissions.api';
import { useSavePcmpEditPermission } from '../../hooks/useSavePcmpEditPermission';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpGrantUserPicker, type PcmpGrantPickedUser } from './PcmpGrantUserPicker';

interface FormValues {
  editAnyStage: boolean;
  proposalCorrection: boolean;
}

interface PcmpEditPermissionDialogProps {
  /** A user chosen from the list (their name is fixed); null opens the picker. */
  user: PcmpGrantPickedUser | null;
  onClose: () => void;
}

const OPTIONS: { name: keyof FormValues; label: string; hint: string }[] = [
  {
    name: 'editAnyStage',
    label: 'Edit from any stage',
    hint: "Change a proposal's institute, student, teacher and payment details at any stage before disbursement.",
  },
  {
    name: 'proposalCorrection',
    label: 'Proposal Correction',
    hint: 'See the Proposal Correction tab, work correction requests and edit while one is open.',
  },
];

/** Where an Admin ticks the PCMP edit grants of one General User; both off withdraws them. */
export function PcmpEditPermissionDialog({ user, onClose }: PcmpEditPermissionDialogProps) {
  const [picked, setPicked] = useState<PcmpGrantPickedUser | null>(user);
  const form = useForm<FormValues>({
    defaultValues: { editAnyStage: false, proposalCorrection: false },
  });
  const { reset } = form;

  const { data: current } = useQuery(pcmpEditGrantQueryOptions(picked?.id ?? null));
  // Choosing a user pre-ticks what they already have.
  useEffect(() => {
    if (current) {
      reset({
        editAnyStage: current.editAnyStage,
        proposalCorrection: current.proposalCorrection,
      });
    }
  }, [current, reset]);

  const { mutate, isPending, error } = useSavePcmpEditPermission(onClose);

  const onSubmit = form.handleSubmit((values) => {
    if (picked) mutate({ userId: picked.id, userName: picked.name, ...values });
  });

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="PCMP edit permissions"
        description={
          user
            ? user.name
            : 'Pick a General User and tick what they may do. Untick both to withdraw.'
        }
        icon={ShieldCheck}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        submitDisabled={picked === null || current === undefined}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Save"
        pendingLabel="Saving..."
      >
        {user ? null : (
          <div className="flex flex-col gap-2">
            <Label htmlFor="pcmp-grant-user">General User</Label>
            <PcmpGrantUserPicker id="pcmp-grant-user" value={picked} onChange={setPicked} />
          </div>
        )}
        {OPTIONS.map((option) => (
          <div key={option.name} className="flex items-start gap-3">
            <Checkbox
              id={`pcmp-grant-${option.name}`}
              className="mt-0.5 size-5"
              disabled={picked === null || current === undefined}
              checked={form.watch(option.name)}
              onCheckedChange={(checked) =>
                form.setValue(option.name, checked === true, { shouldDirty: true })
              }
            />
            <div className="flex flex-col gap-0.5">
              <Label htmlFor={`pcmp-grant-${option.name}`}>{option.label}</Label>
              <p className="text-xs text-muted-foreground">{option.hint}</p>
            </div>
          </div>
        ))}
      </PcmpEditDialog>
    </Form>
  );
}
